import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  useToast,
  VStack,
  Text,
  Checkbox,
  CheckboxGroup,
  Stack,
  Box,
  Divider,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { getAllEmployees } from 'services/api';
import { calculatePayroll } from 'services/payroll.service';

export default function CalculatePayrollModal({
  isOpen,
  onClose,
  period,
  onSuccess,
}) {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const toast = useToast();

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await getAllEmployees();
      if (response.status === 200) {
        // Filtrar solo empleados activos con sueldo
        const activeEmployees = (response.payload.employees || []).filter(
          (emp) => emp.status === 'ACTIVE' && emp.salary > 0
        );
        setEmployees(activeEmployees);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los empleados',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedEmployees(employees.map((emp) => emp.id));
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleSubmit = async () => {
    if (selectedEmployees.length === 0) {
      toast({
        title: 'Error',
        description: 'Seleccione al menos un empleado',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      const response = await calculatePayroll({
        periodId: period.id,
        employeeIds: selectedEmployees,
      });

      if (response.status === 200 || response.status === 201) {
        toast({
          title: 'Éxito',
          description: `Se calcularon ${selectedEmployees.length} liquidaciones correctamente`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onSuccess();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error.response?.data?.message ||
          'No se pudieron calcular las liquidaciones',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Calcular Liquidaciones - {period.month}/{period.year}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                Se calcularán las liquidaciones con IRPF, BPS (15%) y FONASA
                (3%) según el sistema uruguayo.
              </AlertDescription>
            </Alert>

            {loadingEmployees ? (
              <Text textAlign="center" py={4}>
                Cargando empleados...
              </Text>
            ) : employees.length === 0 ? (
              <Text textAlign="center" py={4} color="gray.500">
                No hay empleados activos con sueldo asignado
              </Text>
            ) : (
              <>
                <Box>
                  <Checkbox
                    onChange={handleSelectAll}
                    isChecked={
                      selectedEmployees.length === employees.length &&
                      employees.length > 0
                    }
                    isIndeterminate={
                      selectedEmployees.length > 0 &&
                      selectedEmployees.length < employees.length
                    }
                  >
                    <Text fontWeight="500">Seleccionar todos</Text>
                  </Checkbox>
                </Box>

                <Divider />

                <CheckboxGroup
                  value={selectedEmployees}
                  onChange={setSelectedEmployees}
                >
                  <Stack spacing={3}>
                    {employees.map((employee) => (
                      <Box
                        key={employee.id}
                        p={3}
                        borderWidth="1px"
                        borderRadius="md"
                        _hover={{ bg: 'gray.50' }}
                      >
                        <Checkbox value={employee.id}>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="500">{employee.name}</Text>
                            <Text fontSize="sm" color="gray.500">
                              {employee.document} - Sueldo: ${' '}
                              {employee.salary.toLocaleString('es-UY')}
                            </Text>
                          </VStack>
                        </Checkbox>
                      </Box>
                    ))}
                  </Stack>
                </CheckboxGroup>
              </>
            )}

            {selectedEmployees.length > 0 && (
              <Box
                p={3}
                bg="blue.50"
                borderRadius="md"
                borderWidth="1px"
                borderColor="blue.200"
              >
                <Text fontSize="sm" fontWeight="500">
                  Empleados seleccionados: {selectedEmployees.length}
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isDisabled={selectedEmployees.length === 0 || loadingEmployees}
          >
            Calcular Liquidaciones
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
